/**
 * Infinite scroll loader for supermarket-grid sections.
 *
 * Watches for .supermarket-grid__infinite-container elements on the page
 * and uses an IntersectionObserver on each sentinel to fetch the next
 * paginated page of products from data-next-url, then appends the new
 * cards in place.
 *
 * Re-binds automatically when the DOM changes (tab clicks and filter
 * AJAX swaps trigger fresh containers).
 */
(function () {
  'use strict';

  var ROOT_MARGIN = '800px'; // start loading well before the sentinel hits viewport
  var observed = new WeakSet(); // track containers we've already attached to

  function attachToContainer(container) {
    if (!container || observed.has(container)) return;

    var sentinel = container.querySelector('.supermarket-grid__infinite-sentinel');
    var nextUrl = container.getAttribute('data-next-url');
    if (!sentinel || !nextUrl) return;

    observed.add(container);
    var loading = false;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || loading) return;
        var currentNext = container.getAttribute('data-next-url');
        if (!currentNext) {
          observer.disconnect();
          return;
        }
        loading = true;
        fetchNext(container, currentNext).finally(function () {
          loading = false;
          // After append, the sentinel might have been removed (no more pages)
          // — disconnect in that case.
          var stillHasNext = container.getAttribute('data-next-url');
          if (!stillHasNext) observer.disconnect();
        });
      });
    }, { rootMargin: ROOT_MARGIN });

    observer.observe(sentinel);
  }

  function fetchNext(container, nextUrl) {
    // Preserve any section_id query so Shopify returns just the section,
    // not the whole page HTML. The next URL coming from Shopify's paginate
    // tag is the canonical collection URL with &page=N — we need to add
    // section_id so the section endpoint kicks in.
    var sectionId = container.getAttribute('data-section-id');
    var url = nextUrl;
    if (sectionId && url.indexOf('section_id=') === -1) {
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
    }

    if (window.themeInfiniteScrollDebug) {
      // eslint-disable-next-line no-console
      console.log('[infinite] fetching', url);
    }

    var grid = container.querySelector('.supermarket-grid__list');
    var sentinel = container.querySelector('.supermarket-grid__infinite-sentinel');

    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var newContainer = parsed.querySelector('.supermarket-grid__infinite-container');
        if (!newContainer) {
          if (window.themeInfiniteScrollDebug) {
            console.warn('[infinite] response had no .supermarket-grid__infinite-container');
          }
          container.removeAttribute('data-next-url');
          return;
        }

        // Append new product cards (skip the empty-state if any)
        var newCards = newContainer.querySelectorAll('.supermarket-grid__item');
        if (window.themeInfiniteScrollDebug) {
          // eslint-disable-next-line no-console
          console.log('[infinite] appending', newCards.length, 'cards');
        }
        newCards.forEach(function (card) {
          grid.appendChild(card);
        });

        // Update the next-url for subsequent loads
        var newNext = newContainer.getAttribute('data-next-url') || '';
        container.setAttribute('data-next-url', newNext);

        // If there are no more pages, replace the sentinel with the end state
        if (!newNext) {
          if (sentinel) sentinel.remove();
          var newEnd = newContainer.querySelector('.supermarket-grid__infinite-end');
          if (newEnd) container.appendChild(newEnd.cloneNode(true));
        }

        // Notify the page so any Alpine components inside the new cards
        // get a chance to (re-)initialize. The product cards bring their
        // own initialization, but firing a generic event is helpful for
        // any custom listeners that want to know about appended content.
        document.dispatchEvent(new CustomEvent('tm:infinite-scroll:appended', {
          bubbles: true,
          detail: { count: newCards.length, container: container },
        }));
      })
      .catch(function (err) {
        // eslint-disable-next-line no-console
        console.error('[infinite] fetch failed', err);
        // Don't clear next-url on transient errors; the observer will retry
        // when the sentinel intersects again (e.g., after scroll wiggle).
      });
  }

  function scanAndAttach(root) {
    var scope = root || document;
    var containers = scope.querySelectorAll('.supermarket-grid__infinite-container');
    containers.forEach(attachToContainer);
  }

  // Initial scan on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scanAndAttach(); });
  } else {
    scanAndAttach();
  }

  // Re-scan whenever the supermarket grid content swaps (tab click, filter)
  document.addEventListener('tm:supermarket-grid:content-swapped', function (event) {
    scanAndAttach(event.detail && event.detail.root ? event.detail.root : document);
  });

  // MutationObserver fallback — catches dynamic insertions we didn't know about
  var domObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.addedNodes && m.addedNodes.length) {
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('supermarket-grid__infinite-container')) {
            attachToContainer(node);
          } else if (node.querySelectorAll) {
            var inner = node.querySelectorAll('.supermarket-grid__infinite-container');
            inner.forEach(attachToContainer);
          }
        }
      }
    }
  });
  domObserver.observe(document.body, { childList: true, subtree: true });
})();
